import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { EmdrSetsAPI } from "../infrastructure/api/EmdrSets.api";
import {
    errorCodeFrom,
    type EmdrSetDraft,
    type EmdrSetErrorCode,
    type EmdrSetRecorder,
} from "../domain/emdrSets";

const KEY = "emdr-sets";

function codeOf(error: unknown): EmdrSetErrorCode {
    return errorCodeFrom(isAxiosError(error) ? error.response?.data?.detail : undefined);
}

/** The sets of one session, and the way to add and remove them. */
export function useEmdrSets(sessionId: string | null): EmdrSetRecorder {
    const queryClient = useQueryClient();
    const [error, setError] = useState<EmdrSetErrorCode | null>(null);

    const { data: sets = [], isLoading: loading } = useQuery({
        queryKey: [KEY, sessionId],
        enabled: Boolean(sessionId),
        queryFn: async () => (await EmdrSetsAPI.list(sessionId as string)).data,
    });

    const invalidate = () => queryClient.invalidateQueries({ queryKey: [KEY, sessionId] });

    const recordMutation = useMutation({
        mutationFn: (draft: EmdrSetDraft) => EmdrSetsAPI.record(sessionId as string, draft),
        onSuccess: invalidate,
    });
    const removeMutation = useMutation({
        mutationFn: (id: string) => EmdrSetsAPI.remove(sessionId as string, id),
        onSuccess: invalidate,
    });

    const record = useCallback(
        async (draft: EmdrSetDraft) => {
            if (!sessionId) return;
            setError(null);
            try {
                await recordMutation.mutateAsync(draft);
            } catch (caught) {
                setError(codeOf(caught));
                throw caught;
            }
        },
        [recordMutation, sessionId]
    );

    const remove = useCallback(
        async (id: string) => {
            setError(null);
            try {
                await removeMutation.mutateAsync(id);
            } catch (caught) {
                setError(codeOf(caught));
            }
        },
        [removeMutation]
    );

    // The set form's unsaved ratings, so ending the session can store them.
    const pendingRef = useRef<(() => Promise<void>) | null>(null);
    const holdPending = useCallback((flush: (() => Promise<void>) | null) => {
        pendingRef.current = flush;
    }, []);
    const flushPending = useCallback(async () => {
        await pendingRef.current?.();
    }, []);

    return { sets, loading: Boolean(sessionId) && loading, saving: recordMutation.isPending, error, record, remove, holdPending, flushPending };
}
